import React, { Component } from 'react';
import { XIcon, CheckCircleIcon, ClockIcon, PackageIcon, XCircleIcon, CalendarIcon, UserIcon, MapPinIcon, CreditCardIcon, FileTextIcon, TruckIcon, ExternalLinkIcon, StoreIcon } from 'lucide-react';
interface PrescriptionItem {
  name: string;
  quantity: number;
  price: number;
  image: string;
}
interface TrackingEvent {
  status: string;
  timestamp: string;
  description: string;
}
interface PrescriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  prescription: {
    id: string;
    date: string;
    status: string;
    items: PrescriptionItem[];
    doctor?: string;
    deliveryAddress?: string;
    deliveryCoordinates?: {
      lat: number;
      lng: number;
    };
    pickupBranch?: {
      name: string;
      address: string;
      coordinates: {
        lat: number;
        lng: number;
      };
    };
    pickupTime?: string;
    paymentMethod?: string;
    total?: number;
    prescriptionSource?: 'cash' | 'wasfaty' | 'insurance';
    insuranceProvider?: string;
    wasfatyNumber?: string;
    trackingHistory?: TrackingEvent[];
  };
}
export function PrescriptionDetailsModal({
  isOpen,
  onClose,
  prescription
}: PrescriptionDetailsModalProps) {
  if (!isOpen) return null;
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-4688" data-mp="11fnqeid0s2x5" />;
      case 'ready':
        return <PackageIcon className="w-6 h-6 text-blue-500" data-id="element-4689" data-mp="16ce6v6sql0j3" />;
      case 'in-progress':
        return <ClockIcon className="w-6 h-6 text-yellow-500" data-id="element-4690" data-mp="1bb7n36zer5my" />;
      default:
        return <XCircleIcon className="w-6 h-6 text-red-500" data-id="element-4691" data-mp="1gau13in28pwt" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'ready':
        return 'Ready for Pickup';
      case 'in-progress':
        return 'In Progress';
      default:
        return 'Rejected';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'ready':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'in-progress':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return 'bg-red-50 text-red-700 border-red-200';
    }
  };
  const getPrescriptionSourceBadge = () => {
    if (!prescription.prescriptionSource) return null;
    const badges = {
      cash: {
        label: 'Cash Prescription',
        color: 'bg-gray-100 text-gray-700'
      },
      wasfaty: {
        label: 'Wasfaty',
        color: 'bg-blue-100 text-blue-700'
      },
      insurance: {
        label: 'Insurance',
        color: 'bg-green-100 text-green-700'
      }
    };
    const badge = badges[prescription.prescriptionSource];
    return <div className="mb-4" data-id="element-4692" data-mp="1uzgpfpkjky1r">
        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold ${badge.color}`} data-id="element-4693" data-mp="1q044599bjz1a">
          <FileTextIcon className="w-3.5 h-3.5" data-id="element-4694" data-mp="29mxjr00kh4xd" />
          {badge.label}
          {prescription.prescriptionSource === 'wasfaty' && prescription.wasfatyNumber && <span className="ml-1" data-id="element-4695" data-mp="20lr0wkag3i0y">• {prescription.wasfatyNumber}</span>}
          {prescription.prescriptionSource === 'insurance' && prescription.insuranceProvider && <span className="ml-1" data-id="element-4696" data-mp="1quwar4u2vya8">• {prescription.insuranceProvider}</span>}
        </span>
      </div>;
  };
  const getGoogleMapsLink = (coordinates?: {
    lat: number;
    lng: number;
  }, address?: string) => {
    if (coordinates) {
      return `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
    }
    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return null;
  };
  return <div className="fixed inset-0 z-[200] overflow-y-auto" data-id="element-4697" data-mp="1cubuafbmyejw">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-4698" data-mp="14unxn9pis1mm">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} data-id="element-4699" data-mp="zwe22xzvuz0h"></div>
        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full max-h-[90vh] overflow-y-auto" data-id="element-4700" data-mp="r4c559p0w0uy">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] px-6 py-4 sticky top-0 z-10" data-id="element-4701" data-mp="h3s1y1hy0ma3">
            <div className="flex items-center justify-between" data-id="element-4702" data-mp="c6jrkn31dmje">
              <div data-id="element-4703" data-mp="85g87qls58df">
                <h3 className="text-xl font-bold text-white" data-id="element-4704" data-mp="8699ic1yz5o6">
                  Prescription Details
                </h3>
                <p className="text-sm text-blue-100 mt-1" data-id="element-4705" data-mp="26i6u6vs66rac">{prescription.id}</p>
              </div>
              <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors" data-id="element-4706" data-mp="22gl9wpdb3aq4">
                <XIcon className="w-6 h-6" data-id="element-4707" data-mp="3pem5xxqkqig5" />
              </button>
            </div>
          </div>
          {/* Content */}
          <div className="px-6 py-6 space-y-6" data-id="element-4708" data-mp="3u7fc8q1oxyuc">
            {/* Prescription Source Badge */}
            {getPrescriptionSourceBadge()}
            {/* Status */}
            <div className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(prescription.status)}`} data-id="element-4709" data-mp="31hzxbp6wnlt5">
              <div className="flex items-center" data-id="element-4710" data-mp="36inn67jedwls">
                {getStatusIcon(prescription.status)}
                <div className="ml-3" data-id="element-4711" data-mp="3ak990idwtl84">
                  <p className="font-bold" data-id="element-4712" data-mp="3fhr9dkdv2s4i">
                    {getStatusText(prescription.status)}
                  </p>
                  <p className="text-sm" data-id="element-4713" data-mp="pr4cqvw28eau">
                    {new Date(prescription.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  </p>
                </div>
              </div>
              <CalendarIcon className="w-5 h-5" data-id="element-4714" data-mp="2msa8tbthyipa" />
            </div>
            {/* Prescribed Items with Images and Prices */}
            <div data-id="element-4715" data-mp="2vt8f60rer2b1">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4716" data-mp="2vsq9yemrpdqz">
                <FileTextIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4717" data-mp="283nhscuic2i8" />
                Prescribed Items
              </h4>
              <div className="space-y-3" data-id="element-4718" data-mp="2352s37cvt181">
                {prescription.items.map((item, index) => <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4719" data-mp="2htqkx5vcewua" data-mpi={index}>
                    <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" data-id="element-4720" data-mp="1pcwcbmdn4w94" />
                    <div className="flex-1" data-id="element-4721" data-mp="3w1ewb36i1fel">
                      <h5 className="font-medium text-gray-900 mb-1" data-id="element-4722" data-mp="3w0mydlpfj5l5">
                        {item.name}
                      </h5>
                      <p className="text-sm text-gray-600" data-id="element-4723" data-mp="4otyulk3m0wn">
                        Quantity: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-4724" data-mp="3r3d8y621itxb">
                      <p className="text-lg font-bold text-[#1E2B55]" data-id="element-4725" data-mp="3qzzc1dc5bcx9">
                        SAR {item.price.toFixed(2)}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Doctor Info */}
            {prescription.doctor && <div data-id="element-4726" data-mp="ekf4qusaflbo">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4727" data-mp="ejvnzhedj2z8">
                  <UserIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4728" data-mp="2ubjht3y3cfdt" />
                  Prescribed By
                </h4>
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-4729" data-mp="2zbxfpt5za8y4">
                  <p className="text-sm text-gray-700" data-id="element-4730" data-mp="3q0zezibibli6">{prescription.doctor}</p>
                </div>
              </div>}
            {/* Delivery Address or Pickup Branch */}
            {prescription.deliveryAddress && <div data-id="element-4731" data-mp="3h1m5ipnduwxg">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4732" data-mp="3h0t506f0p3nr">
                  <MapPinIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4733" data-mp="3c2rl5j9rp0b8" />
                  Delivery Address
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4734" data-mp="dlx3xweba84i">
                  <p className="text-sm text-gray-700 mb-3" data-id="element-4735" data-mp="8l9ehf5qdzce">
                    {prescription.deliveryAddress}
                  </p>
                  {getGoogleMapsLink(prescription.deliveryCoordinates, prescription.deliveryAddress) && <a href={getGoogleMapsLink(prescription.deliveryCoordinates, prescription.deliveryAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1E2B55] hover:text-[#fdc300] font-medium text-sm" data-id="element-4736" data-mp="wbmxkg6c8nuu">
                      <MapPinIcon className="w-4 h-4" data-id="element-4737" data-mp="1ds0t498owi0p" />
                      View on Google Maps
                      <ExternalLinkIcon className="w-4 h-4" data-id="element-4738" data-mp="1htm41p1nym89" />
                    </a>}
                </div>
              </div>}
            {prescription.pickupBranch && <div data-id="element-4739" data-mp="1rjp6sgyl7ejs">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4740" data-mp="1rj5mwupdmg34">
                  <StoreIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4741" data-mp="1wi8tijl08ud7" />
                  Pickup Branch
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4742" data-mp="21ieh9oo5lwq0">
                  <p className="font-medium text-gray-900 mb-1" data-id="element-4743" data-mp="25ioxdu5nu3il">
                    {prescription.pickupBranch.name}
                  </p>
                  <p className="text-sm text-gray-700 mb-3" data-id="element-4744" data-mp="371d8wnua68r1">
                    {prescription.pickupBranch.address}
                  </p>
                  {prescription.pickupTime && <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-lg" data-id="element-4745" data-mp="3gx6h5v0yvg30">
                      <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-4746" data-mp="3lpge3v0i1ges" />
                      <span className="text-sm font-medium text-blue-900" data-id="element-4747" data-mp="3qoaukqazusvq">
                        Pickup Time: {prescription.pickupTime}
                      </span>
                    </div>}
                  <a href={getGoogleMapsLink(prescription.pickupBranch.coordinates, prescription.pickupBranch.address)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1E2B55] hover:text-[#fdc300] font-medium text-sm" data-id="element-4748" data-mp="3is9e69d2v9n">
                    <MapPinIcon className="w-4 h-4" data-id="element-4749" data-mp="22rjtlh3kx97p" />
                    View on Google Maps
                    <ExternalLinkIcon className="w-4 h-4" data-id="element-4750" data-mp="1xyicd2fwgxky" />
                  </a>
                </div>
              </div>}
            {/* Tracking History or Pickup Time */}
            {prescription.trackingHistory && prescription.trackingHistory.length > 0 && <div data-id="element-4751" data-mp="2lnjop0russvi">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4752" data-mp="2lo34obxy176w">
                    <TruckIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4753" data-mp="2gqb3ssfbmaqk" />
                    Delivery Tracking
                  </h4>
                  <div className="space-y-4" data-id="element-4754" data-mp="2bsi0q89bdwui">
                    {prescription.trackingHistory.map((event, index) => <div key={index} className="flex gap-4" data-id="element-4755" data-mp="10b9rn9nq3sk0" data-mpi={index}>
                        <div className="flex flex-col items-center" data-id="element-4756" data-mp="109f73wgd62gg">
                          <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-4757" data-mp="vbnj3diub0hj" />
                          {index < prescription.trackingHistory.length - 1 && <div className="w-0.5 h-full bg-gray-300 mt-1" data-id="element-4758" data-mp="39k6id9l0k4vl" />}
                        </div>
                        <div className="flex-1 pb-4" data-id="element-4759" data-mp="3ei80nsam8060">
                          <div className="flex items-center justify-between mb-1" data-id="element-4760" data-mp="3el40jm60wyx3">
                            <p className={`font-medium ${index === 0 ? 'text-green-700' : 'text-gray-700'}`} data-id="element-4761" data-mp="2zsa6mvq08g8w">
                              {event.status}
                            </p>
                            <span className="text-xs text-gray-500" data-id="element-4762" data-mp="2vs9qn6n9bwzq">
                              {new Date(event.timestamp).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-4763" data-mp="2c4ww881noitu">
                            {event.description}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>}
            {/* Payment Method */}
            {prescription.paymentMethod && <div data-id="element-4764" data-mp="2uwfy2bjy1qua">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center" data-id="element-4765" data-mp="2uwzhxxt5mpay">
                  <CreditCardIcon className="w-5 h-5 mr-2 text-[#1E2B55]" data-id="element-4766" data-mp="2pzfty66nmy5t" />
                  Payment Method
                </h4>
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-4767" data-mp="34vx4iv70s8rb">
                  <p className="text-sm text-gray-700" data-id="element-4768" data-mp="2zxn5g9i6co0f">
                    {prescription.paymentMethod}
                  </p>
                </div>
              </div>}
            {/* Total */}
            {prescription.total && <div className="border-t border-gray-200 pt-4" data-id="element-4769" data-mp="126x343iifoli">
                <div className="flex items-center justify-between" data-id="element-4770" data-mp="1711c0sbc66p8">
                  <span className="text-lg font-bold text-gray-900" data-id="element-4771" data-mp="1bzclv31msh7g">Total</span>
                  <span className="text-2xl font-bold text-[#1E2B55]" data-id="element-4772" data-mp="eb2j2lu0tpfa">
                    SAR {prescription.total.toFixed(2)}
                  </span>
                </div>
              </div>}
          </div>
          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex gap-3 sticky bottom-0" data-id="element-4773" data-mp="o87ab02q4b2y">
            <button onClick={onClose} className="flex-1 bg-white border border-gray-300 text-gray-700 font-medium py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-4774" data-mp="t8uyldzkidth">
              Close
            </button>
            {prescription.status === 'delivered' && <button className="flex-1 bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white font-medium py-2.5 px-4 rounded-lg transition-colors" data-id="element-4775" data-mp="3p5stnthawge9">
                Reorder
              </button>}
          </div>
        </div>
      </div>
    </div>;
}